import { z } from 'zod';

export const ProblemDetailsSchema = z.object({
  type: z.string(),
  title: z.string(),
  status: z.number().int(),
  detail: z.string(),
  instance: z.string(),
  request_id: z.string().optional(),
});

export const ValidationProblemDetailsSchema = ProblemDetailsSchema.extend({
  invalid_params: z.array(
    z.object({
      name: z.string(),
      reason: z.string(),
    }),
  ),
});

const BASE_URI = 'https://api.shiftagent.com/errors';

export type ProblemDetails = z.infer<typeof ProblemDetailsSchema>;

export function createProblemDetails(opts: {
  type: string;
  title: string;
  status: number;
  detail: string;
  instance: string;
  request_id?: string | undefined;
  extensions?: Record<string, unknown> | undefined;
}): ProblemDetails & Record<string, unknown> {
  const { extensions, ...base } = opts;
  const typeUri = base.type.startsWith('https://') ? base.type : `${BASE_URI}/${base.type}`;
  return {
    ...base,
    type: typeUri,
    ...extensions,
  };
}

function helper(
  type: string,
  title: string,
  status: number,
  detail: string,
  instance: string,
  request_id?: string,
): ProblemDetails {
  return createProblemDetails({ type, title, status, detail, instance, request_id });
}

export const notFound = (detail: string, instance: string, request_id?: string) =>
  helper('not_found', 'Not Found', 404, detail, instance, request_id);

export const badRequest = (detail: string, instance: string, request_id?: string) =>
  helper('bad_request', 'Bad Request', 400, detail, instance, request_id);

export const unauthorized = (detail: string, instance: string, request_id?: string) =>
  helper('unauthorized', 'Unauthorized', 401, detail, instance, request_id);

export const forbidden = (detail: string, instance: string, request_id?: string) =>
  helper('forbidden', 'Forbidden', 403, detail, instance, request_id);

export const conflict = (detail: string, instance: string, request_id?: string) =>
  helper('conflict', 'Conflict', 409, detail, instance, request_id);

export const unprocessableEntity = (detail: string, instance: string, request_id?: string) =>
  helper('unprocessable_entity', 'Unprocessable Entity', 422, detail, instance, request_id);

export const tooManyRequests = (detail: string, instance: string, request_id?: string) =>
  helper('too_many_requests', 'Too Many Requests', 429, detail, instance, request_id);

export const internalServerError = (detail: string, instance: string, request_id?: string) =>
  helper('internal_server_error', 'Internal Server Error', 500, detail, instance, request_id);
